import React, { useState } from "react";
import { UserRole } from "../../../services/UserService";
import { ChevronDown, User } from "lucide-react";

interface UpdateRoleModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedRole: UserRole) => void;
  email: string;
  currentRole: UserRole;
  organizationName: string;
  memberCount: number;
  currentUserRole: UserRole;
}

const UpdateRoleModal: React.FC<UpdateRoleModalProps> = ({
  open,
  onClose,
  onConfirm,
  email,
  currentRole,
  organizationName,
  memberCount,
  currentUserRole,
}) => {
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);

  const availableRoles = Object.values(UserRole).filter(
    (role) =>
      role !== UserRole.Owner &&
      role.toLowerCase() !== currentRole.toLowerCase()
  );

  const formatRole = (role: string): string => {
    if (role.toLowerCase() === "superadmin") return "Super Admin";
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };

  if (!open) return null;

  const handleConfirm = () => {
    if (selectedRole) onConfirm(selectedRole);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-background-paper dark:bg-background-paperDark p-6 rounded-lg shadow-lg w-full max-w-lg">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-bold text-text-primary dark:text-text-primaryDark">
            Update Role
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition"
          >
            ✕
          </button>
        </div>

        <div className="flex items-center mb-6 bg-background-panel dark:bg-background-panelDark rounded-lg p-4">
          <User className="w-12 h-12 text-secondary mr-4" />
          <div>
            <p className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
              {email}
            </p>
            <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
              Current Role: <strong>{formatRole(currentRole)}</strong>
            </p>
          </div>
        </div>

        <div className="bg-background-panel dark:bg-background-panelDark p-4 rounded-md mb-6">
          <p className="text-sm text-text-primary dark:text-text-primaryDark">
            <strong>Organization:</strong> {organizationName}
          </p>
          <p className="text-sm text-text-primary dark:text-text-primaryDark">
            <strong>Members:</strong> {memberCount}
          </p>
          <p className="text-sm text-text-primary dark:text-text-primaryDark">
            <strong>Your Role:</strong> {formatRole(currentUserRole)}
          </p>
        </div>

        <div className="relative mb-6">
          <select
            value={selectedRole || ""}
            onChange={(e) => setSelectedRole(e.target.value as UserRole)}
            className="w-full p-3 border border-divider dark:border-divider-dark rounded-md appearance-none bg-background-paper dark:bg-background-paperDark text-text-primary dark:text-text-primaryDark focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
          >
            <option value="" disabled>
              Select a role
            </option>
            {availableRoles.map((role) => (
              <option key={role} value={role}>
                {formatRole(role)}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-text-secondary dark:text-text-secondaryDark">
            <ChevronDown className="w-5 h-5" />
          </div>
        </div>

        <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-6">
          Roles determine permissions within the organization. Ensure you assign
          roles appropriately to maintain a structured environment.
        </p>

        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="py-2 px-6 rounded-md text-sm font-medium border border-divider dark:border-divider-dark text-text-secondary dark:text-text-secondaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="py-2 px-6 rounded-md text-sm font-medium bg-secondary text-white hover:bg-secondary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!selectedRole}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateRoleModal;
